/* Geometric ornaments and custom icons. All stroke/fill with currentColor so
   they theme with the text colour. Icons share lucide's 24×24 grid. */
import type { ReactNode } from 'react';

type IconProps = { size?: number; className?: string; strokeWidth?: number };
const Svg = ({ size = 24, className, strokeWidth = 1.8, children }: IconProps & { children: ReactNode }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
    {children}
  </svg>
);

/** Rubʿ al-ḥizb / khātam: two overlapping squares. */
export function StarIcon({ hollow, ...p }: IconProps & { hollow?: boolean }) {
  // hollow: no centre dot, so a number can sit inside (the surah list)
  return <Svg {...p}><rect x="5.5" y="5.5" width="13" height="13" /><rect x="5.5" y="5.5" width="13" height="13" transform="rotate(45 12 12)" />{!hollow && <circle cx="12" cy="12" r="2.4" />}</Svg>;
}
export function MosqueIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M5 20V12a7 7 0 0 1 14 0v8" />
      <path d="M12 5c-1.6.9-2.5 2-2.5 3h5c0-1-.9-2.1-2.5-3z" />
      <path d="M12 2v3" />
      <path d="M2.5 20V9.5l1-2 1 2V20M19.5 20V9.5l1-2 1 2V20" />
      <path d="M2 20h20" />
      <path d="M10 20v-3.5a2 2 0 0 1 4 0V20" />
    </Svg>
  );
}
export function MushafIcon(p: IconProps) {
  // Open muṣḥaf resting on a raḥl (X-shaped stand)
  return (
    <Svg {...p}>
      <path d="M12 7c-2-1.6-5-2-8-1.4v9.6c3-.6 6-.2 8 1.4 2-1.6 5-2 8-1.4V5.6C17 5 14 5.4 12 7z" />
      <path d="M12 7v9.6" />
      <path d="M6 18.5l12 3M18 18.5l-12 3" />
    </Svg>
  );
}
export function MinaretIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M12 1.8v1.8" />
      <path d="M9.5 7.5L12 3.6l2.5 3.9z" />
      <path d="M10 7.5h4v14h-4z" />
      <path d="M8.5 11h7M9 15.5h6" />
      <path d="M11 21.5v-2.5a1 1 0 0 1 2 0v2.5" />
      <path d="M5 21.5h14" />
    </Svg>
  );
}
export function TasbihIcon(p: IconProps) {
  // Prayer beads looped, with the imām bead and tassel
  const beads = Array.from({ length: 11 }, (_, i) => {
    const a = (Math.PI * 2 * i) / 12 - Math.PI / 2 + Math.PI / 12;
    return [12 + Math.cos(a) * 7, 9.5 + Math.sin(a) * 7] as const;
  });
  return (
    <Svg {...p}>
      {beads.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="1.3" />)}
      <path d="M12 16.5v2" />
      <path d="M10.5 18.5h3l-.5 3.5h-2z" />
    </Svg>
  );
}
export function LanternIcon(p: IconProps) {
  // Fānūs — the Ramadan lantern
  return (
    <Svg {...p}>
      <path d="M12 1.5v2" />
      <path d="M9 3.5h6l1.5 3h-9z" />
      <path d="M7.5 6.5h9l-1 10h-7z" />
      <path d="M12 6.5v10M9.8 9.5l4.4 4M14.2 9.5l-4.4 4" opacity=".7" />
      <path d="M8.5 16.5h7l-1.5 3h-4z" />
      <path d="M12 19.5v2.5" />
    </Svg>
  );
}
export function CrescentIcon(p: IconProps) {
  return <Svg {...p}><path d="M15 3.5a8.5 8.5 0 1 0 5.5 14.8A7 7 0 1 1 15 3.5z" /><path d="M18 6.5l.6 1.4 1.5.2-1.1 1 .3 1.5-1.3-.8-1.3.8.3-1.5-1.1-1 1.5-.2z" /></Svg>;
}

/** Section divider: hairlines either side of an 8-point star. */
export function Ornament({ className = '' }: { className?: string }) {
  return (
    <div className={`flex items-center gap-3 text-gold ${className}`} aria-hidden>
      <span className="h-px flex-1 bg-gradient-to-r from-transparent to-gold/50" />
      <StarIcon size={14} strokeWidth={1.6} />
      <span className="h-px flex-1 bg-gradient-to-l from-transparent to-gold/50" />
    </div>
  );
}

/** Mihrab arch — a pointed (ogee-ish) arch outline to frame headers and cards. */
export function Arch({ className = '', children }: { className?: string; children?: ReactNode }) {
  return (
    <div className={`relative ${className}`}>
      <svg viewBox="0 0 200 60" preserveAspectRatio="none" className="absolute inset-x-0 top-0 h-12 w-full text-gold/45" aria-hidden>
        <path d="M2 60V40C2 22 40 16 70 12c16-2 26-6 30-11 4 5 14 9 30 11 30 4 68 10 68 28v20" fill="none" stroke="currentColor" strokeWidth="1.4" vectorEffect="non-scaling-stroke" />
      </svg>
      {children}
    </div>
  );
}

/* 8-point star tessellation as a CSS background (khātam sulaymānī tiles). */
export const PATTERN_SVG = (color: string, opacity = 0.08) => {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='56' height='56' viewBox='0 0 56 56'><g fill='none' stroke='${color}' stroke-opacity='${opacity}' stroke-width='1'><rect x='16' y='16' width='24' height='24'/><rect x='16' y='16' width='24' height='24' transform='rotate(45 28 28)'/><path d='M0 0l11 11M56 0L45 11M0 56l11-11M56 56L45 45M28 0v4M28 52v4M0 28h4M52 28h4'/></g></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};
